import type { ErrorCode } from '@good-season/schema';

/**
 * The queue behind `useDialog()`.
 *
 * Kept as a plain store with no React in it, for two reasons. It makes the part
 * that matters (a promise resolves exactly once, with the right value, whether
 * the user answered or dismissed) testable without rendering anything. And it
 * gives the imperative API somewhere to live that does not care how many
 * dialogs are on screen: requests queue, and the next one opens when the
 * current one is answered.
 *
 * Every option here is copy the CALL SITE writes, or an error code. No raw
 * error string, status code, or exception message can be passed through: there
 * is no field for one.
 */

/**
 * Optional fields spell `| undefined` explicitly.
 *
 * `exactOptionalPropertyTypes` is on repo-wide, which means a bare `body?:
 * string` REJECTS `{ body: maybeBody }` where `maybeBody` is `string |
 * undefined`. That is the ordinary shape of a call site building options
 * conditionally, so the strict spelling made the public API awkward to use for
 * no safety gained. Every other props type in this directory already spells it
 * this way; these three were the exception rather than the rule.
 */
export interface AlertOptions {
  /** Overrides the copy for `code`. One of `title` or `code` should be set. */
  readonly title?: string | undefined;
  readonly body?: string | undefined;
  /** Maps to themed copy through `errorCopyFor()`. */
  readonly code?: ErrorCode | undefined;
  /** The six character code from the API response, shown so it can be sent on. */
  readonly diagnostic?: string | undefined;
  readonly confirmLabel?: string | undefined;
}

export interface ConfirmOptions {
  readonly title: string;
  readonly body?: string | undefined;
  readonly confirmLabel?: string | undefined;
  readonly cancelLabel?: string | undefined;
  /** `danger` styles the confirm button destructively. Voiding a play, say. */
  readonly tone?: 'default' | 'danger' | undefined;
}

export interface PromptOptions {
  readonly title: string;
  readonly body?: string | undefined;
  /** Labels the input. Always rendered: a bare input is a guessing game. */
  readonly label: string;
  readonly placeholder?: string | undefined;
  readonly defaultValue?: string | undefined;
  /** Numeric keypad on a phone, and tabular figures in the field. */
  readonly numeric?: boolean | undefined;
  readonly confirmLabel?: string | undefined;
  readonly cancelLabel?: string | undefined;
  /**
   * When set, the confirm button stays disabled until what is typed, trimmed,
   * equals this exactly (case included). For a deliberate, hard-to-undo act,
   * such as typing a team's name to remove it (ADR-0095).
   */
  readonly mustMatch?: string | undefined;
  /** `danger` styles the confirm button destructively, as on ConfirmOptions. */
  readonly tone?: 'default' | 'danger' | undefined;
}

interface EntryBase {
  readonly id: string;
}
export interface AlertEntry extends EntryBase {
  readonly kind: 'alert';
  readonly options: AlertOptions;
}
export interface ConfirmEntry extends EntryBase {
  readonly kind: 'confirm';
  readonly options: ConfirmOptions;
}
export interface PromptEntry extends EntryBase {
  readonly kind: 'prompt';
  readonly options: PromptOptions;
}
export type DialogEntry = AlertEntry | ConfirmEntry | PromptEntry;

/**
 * One answer shape for all three kinds, so there is no per-kind resolver to get
 * wrong and no unreachable branch to pretend to test. `alert` ignores it,
 * `confirm` reads `confirmed`, `prompt` reads `value` only when confirmed.
 */
export interface DialogResult {
  readonly confirmed: boolean;
  readonly value: string | null;
}

/** Escape, the scrim, the close button, and Cancel all mean this. */
export const DISMISSED: DialogResult = { confirmed: false, value: null };

/**
 * What a call site gets. The store's plumbing is not part of it.
 *
 * Declared as function properties rather than methods so that every member is
 * safe to pass around detached, which `useSyncExternalStore` does with
 * `subscribe` and `getCurrent`.
 */
export interface DialogApi {
  readonly alert: (options: AlertOptions) => Promise<void>;
  readonly confirm: (options: ConfirmOptions) => Promise<boolean>;
  readonly prompt: (options: PromptOptions) => Promise<string | null>;
}

export interface DialogStore extends DialogApi {
  readonly subscribe: (listener: () => void) => () => void;
  /** The entry on screen, or null. Stable between changes, for useSyncExternalStore. */
  readonly getCurrent: () => DialogEntry | null;
  /** Answer an entry. Ignores an id that has already been answered. */
  readonly settle: (id: string, result: DialogResult) => void;
  /** Settle every waiting promise at once, for provider teardown. */
  readonly settleAll: (result: DialogResult) => void;
}

let sequence = 0;
function nextId(): string {
  sequence += 1;
  return `gs-dialog-${String(sequence)}`;
}

interface Queued {
  readonly entry: DialogEntry;
  readonly settle: (result: DialogResult) => void;
}

export function createDialogStore(): DialogStore {
  let queue: readonly Queued[] = [];
  const listeners = new Set<() => void>();

  const emit = (): void => {
    for (const listener of listeners) listener();
  };

  const enqueue = (entry: DialogEntry, settle: (result: DialogResult) => void): void => {
    queue = [...queue, { entry, settle }];
    emit();
  };

  return {
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },

    getCurrent: () => queue[0]?.entry ?? null,

    alert: (options) =>
      new Promise<void>((resolve) => {
        enqueue({ id: nextId(), kind: 'alert', options }, () => {
          resolve();
        });
      }),

    confirm: (options) =>
      new Promise<boolean>((resolve) => {
        enqueue({ id: nextId(), kind: 'confirm', options }, (result) => {
          resolve(result.confirmed);
        });
      }),

    prompt: (options) =>
      new Promise<string | null>((resolve) => {
        enqueue({ id: nextId(), kind: 'prompt', options }, (result) => {
          resolve(result.confirmed ? result.value : null);
        });
      }),

    settle: (id, result) => {
      const answered = queue.find((queued) => queued.entry.id === id);
      if (answered === undefined) return;

      // Remove before resolving, so a handler that opens another dialog from
      // the resolution sees a consistent queue.
      queue = queue.filter((queued) => queued.entry.id !== id);
      answered.settle(result);
      emit();
    },

    /**
     * Settle everything still waiting, for teardown.
     *
     * Without this, unmounting the provider leaves every pending promise hanging
     * forever, which contradicts the guarantee at the top of this file. It is
     * not a crash and it has no symptom: the caller simply never continues.
     *
     * The case that matters is `try { if (await confirm(...)) ... } finally {
     * releaseLock() }`. If an ErrorBoundary above the provider catches while
     * that dialog is open, the `finally` never runs, the lock is never
     * released, and the offline queue stalls silently. Offline sync is one of
     * the three surfaces ROADMAP.md sends through /adjudicate, so a silent
     * stall there is the expensive kind.
     *
     * Dismissal is the right result: nobody answered.
     */
    settleAll: (result) => {
      const pending = queue;
      queue = [];
      for (const queued of pending) queued.settle(result);
      emit();
    },
  };
}
